<template>
    <v-form v-model="isValid">
        <v-row no-gutters style="min-width: 700px">
            <v-alert
                v-model="notAvailable"
                type="error"
                text="Ce produit est en rupture de stock"
            />
        </v-row>
        <v-row no-gutters>
            <v-col cols="3" v-if="stockOperationType === 'OUTPUT'" class="pr-1">
                <v-select-filled
                    v-model="operateBy"
                    :items="[
                        { title: 'Produit', value: 'Product' },
                        { title: 'Famille', value: 'Family' },
                    ]"
                    label="Opération selon"
                    data-cy="editStockOperation-family"
                />
            </v-col>
            <v-col :cols="stockOperationType === 'OUTPUT' ? 'xs9 pl-1' : 'xs12'">
                <oss-date-picker-filled
                    v-model="date"
                    :filterWithoutClosedAccountingPeriods="true"
                    hide-details
                    :textFieldReadOnly="false"
                    :readonly="readonly"
                />
            </v-col>
        </v-row>
        <v-row no-gutters class="py-2">
            <v-col
                class="pr-1"
                :cols="showPrice ? '3' : showSalesPrice ? '4' : '6'"
                v-if="operateBy === 'Product'"
            >
                <v-select-filled
                    v-model="stockProductId"
                    :items="products"
                    label="Produit"
                    item-value="id"
                    data-cy="editStockOperation-product"
                    :readonly="readonly"
                    @update:model-value="computeQuantities()"
                    :item-title="(item) => item.title"
                />
            </v-col>
            <v-col class="pr-1" :cols="showPrice ? '3' : '6'" v-else>
                <v-select-filled
                    v-model="productTagId"
                    :items="productTags"
                    item-value="id"
                    label="Famille"
                    data-cy="editStockOperation-family"
                    @update:model-value="updateQuantity()"
                    :readonly="readonly"
                    :item-title="(item) => item.name"
                />
            </v-col>
            <v-col cols="3" class="pr-1" v-if="withQuantity">
                <oss-text-field-filled
                    v-model="quantity"
                    label="Quantité achetée"
                    data-cy="editStockOperation-quantity"
                    :rules="[(v) => quantity > 0 || $t('settings.positiveNumber')]"
                    :readonly="readonly"
                />
            </v-col>
            <v-col :cols="showSalesPrice ? '4' : '6'" class="pr-1" v-else-if="withProductQuantity">
                <oss-text-field-filled
                    v-model="productQuantity"
                    label="Quantité de produit"
                    data-cy="editStockOperation-productQuantity"
                    :rules="[(v) => productQuantity > 0 || $t('settings.positiveNumber')]"
                    :readonly="readonly"
                />
            </v-col>
            <v-col cols="3" class="pr-1" v-if="showPrice">
                <v-text-field-filled
                    v-model.number="stockOperation.unitPrice"
                    suffix="&#x20ac;"
                    type="number"
                    label="Prix d'achat HT unitaire"
                    data-cy="editStockOperation-unitPrice"
                    :readonly="readonly"
                />
            </v-col>
            <v-col cols="3" class="pl-1" v-if="showPrice">
                <oss-text-field-filled
                    v-model="stockOperation.price"
                    suffix="&#x20ac;"
                    label="Prix"
                    data-cy="editStockOperation-price"
                    :readonly="readonly"
                />
            </v-col>
            <v-col cols="4" class="pl-1" v-if="showSalesPrice">
                <oss-text-field-filled
                    v-model="stockOperation.salesPrice"
                    suffix="&#x20ac;"
                    label="Prix de vente"
                    data-cy="editStockOperation-sales-price"
                    :readonly="readonly"
                />
            </v-col>
        </v-row>
        <template v-if="withSubproducts">
            <v-row no-gutters><v-divider /></v-row>
            <template v-for="(item, index) in quantities">
                <v-row no-gutters class="pt-2" v-if="index % 4 === 0">
                    <v-col cols="3" v-for="i in 4" :key="index + i">
                        <oss-text-field-filled
                            v-if="quantities[index + i - 1]"
                            :label="'Quantité : ' + quantities[index + i - 1].quantityName"
                            :class="(i > 1 ? 'ml-1 ' : '') + 'oss-dark'"
                            :rules="[(v) => v >= 0 || $t('settings.positiveNumber')]"
                            :readonly="readonly"
                            v-model="quantities[index + i - 1].quantity"
                        />
                    </v-col>
                </v-row>
            </template>
        </template>
    </v-form>
</template>

<script lang="ts">
import gql from "graphql-tag";
import moment from "moment-timezone";
import { Apollo } from "@/decorators";

import SAVE_STOCK_OPERATION from "../../graphql/stock/SaveStockOperation.gql";
import STOCK_OPERATION from "../../graphql/stock/StockOperation.gql";
import STOCK_BALANCE from "../../graphql/stock/StockBalance.gql";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

@Component({
    emits: ["stockOperation-created", "operation-saved", "stockOperation"],
})
export class StockOperationEdition extends Vue {
    @Prop({ default: "" }) id: string;
    @Prop({ default: "" }) readonly type: string;
    @Prop({ default: "" }) companyId: string;
    @Prop({ default: null }) actions: any;

    stockProductId = "";
    maxQuantity = 0;
    quantity = 0;
    productQuantity = 0;

    saveProduct = true;
    notAvailable = false;
    saveLoading: boolean = false;

    operateBy = "Product";
    productTagId = "";

    date = moment().format();

    defaultStockOperation = {
        id: "",
        // type: '',
        stockProduct: {
            id: "",
            title: "",
            data: null,
        },
        price: 0,
        unitPrice: 0.0,
        productQuantity: 0,
        quantity: 0,
        parent: null,
        accountingLine: null,
        document: null,
        salesPrice: 0,
    };

    quantities = [];

    isValid: boolean = false;

    stockOperationType_: string = "";

    get stockOperationType() {
        if (!this.id) {
            return this.type;
        }
        return this.stockOperationType_;
    }

    set stockOperationType(value) {
        this.stockOperationType_ = value;
    }

    get readonly() {
        if (this.stockOperation?.parent) {
            return true;
        }
        if (this.stockOperation?.accountingLine) {
            return true;
        }
        if (this.stockOperation?.document) {
            return true;
        }
        return false;
    }

    computeQuantities() {
        debugger;
        const quantities = this.currentProduct?.data?.quantities || [];
        if (quantities) {
            this.quantities = quantities.map((q) => ({
                quantityName: q.quantityName,
                quantity: 0,
            }));
            if (this.stockOperation?.data?.quantities?.length) {
                for (const qt of this.quantities) {
                    const soQ = this.stockOperation.data.quantities.find(
                        (q) => q.quantityName === qt.quantityName,
                    );
                    qt.quantity = soQ?.quantity || 0;
                }
            }
        }
    }

    // async getUnitPrice() {
    //     let prod = this.products.find(p => p.id === this.stockProductId)
    //     if(prod && !this.id){
    //         this.stockOperation.unitPrice = prod.unitPrice
    //     }
    //     await this.updateQuantity()
    // }

    async updateQuantity() {
        return;
        if (this.stockOperationType === "OUTPUT" && this.stockProductId) {
            // debugger
            this.maxQuantity = 0;
            let productId = "";
            let productTagId = "";
            if (this.stockProductId && this.operateBy === "Product") {
                productId = this.stockProductId;
            } else if (this.productTagId && this.operateBy === "Family") {
                productTagId = this.productTagId;
            }
            const result = await this.$apollo.query({
                query: STOCK_BALANCE,
                variables: {
                    selectedCompanyId: this.companyId,
                    productId: productId,
                    productTagId: productTagId,
                },
            });
            if (!result.data.stockBalance.stocks.length) {
                if (this.id) {
                    this.maxQuantity = this.quantity;
                    this.notAvailable = false;
                } else {
                    this.notAvailable = true;
                }
            } else {
                this.notAvailable = false;
                if (this.operateBy === "Product") {
                    this.maxQuantity = result.data.stockBalance.stocks[0].available;
                } else {
                    this.maxQuantity = result.data.stockBalance.stocks.reduce(
                        (partialSum, el) => partialSum + el.available,
                        0,
                    );
                }

                if (this.quantity && this.id) {
                    this.maxQuantity += this.quantity;
                }
            }
        } else {
            this.maxQuantity = 0;
            this.notAvailable = false;
        }
    }

    get boutonTile() {
        let title = !this.id ? "Créer l'opération" : "Modifier l'opération";
        if (this.stockOperationType === "INPUT") {
            title += " (Entrée)";
        } else {
            title += " (Sortie)";
        }
        return title;
    }

    beforeMount() {
        if (this.actions) {
            this.actions.action.title = () => {
                return this.boutonTile;
            };
            this.actions.action.disabled = () => {
                return this.disabledSave;
            };
            this.actions.action.function = () => {
                this.save();
            };
            this.actions.action.loading = () => {
                return this.saveLoading;
            };
        }
    }

    get quantityLabel() {
        let label = "Quantité";
        if (this.stockOperationType === "INPUT") {
            label += " d'Entrée";
        } else {
            label += "de sortie";
        }
        return label;
    }

    async createStockOperation() {
        if (this.saveProduct) {
            await this.save();
        }
        this.$emit("stockOperation-created");
        this.resetStockOperation();
    }

    @Apollo({
        query: gql`
            query products($companyId: ID!, $stock: Boolean!) {
                invoiceProducts(where: { company: { id: $companyId }, stock: $stock }) {
                    id
                    reference
                    title
                    unitPrice
                    outDated
                    type
                    family
                    tag {
                        id
                        name
                    }
                    unitQuantity
                    data
                }
            }
        `,
        variables() {
            this.productLoading = true;
            return {
                companyId: this.companyId,
                stock: true,
            };
        },
        skip() {
            return !this.companyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (data) {
                this.productLoading = false;
                for (const p of data.invoiceProducts) {
                    if (p.outDated === null) {
                        p.outDated = true;
                    }
                }
                this.products = data.invoiceProducts.sort(function (x, y) {
                    return x.outDated === y.outDated ? 0 : x.outDated ? 1 : -1;
                });
                const allTags = data.invoiceProducts
                    .map((ip) => {
                        return ip.tag;
                    })
                    .filter((ip) => ip);
                const productTags = [];
                allTags.forEach((tag) => {
                    if (!productTags.find((pt) => pt.id === tag.id)) {
                        this.productTags.push(tag);
                    }
                });
                this.productTags = productTags;
            }
        },
    })
    products = [];

    productTags = [];

    @Apollo({
        query() {
            return STOCK_OPERATION;
        },
        variables() {
            return {
                id: this.id,
            };
        },
        // debounce: 500,
        skip() {
            return !this.id;
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loadingDocument = loading;
            if (!loading) {
                if (data && data.stockOperation) {
                    this.date = data.stockOperation.date;
                    this.stockOperationType =
                        !data.stockOperation.productQuantity ||
                        data.stockOperation.productQuantity > 0 ||
                        !data.stockOperation.quantity ||
                        data.stockOperation.quantity > 0
                            ? "INPUT"
                            : "OUTPUT";
                    this.quantity = Math.abs(data.stockOperation.quantity);
                    this.productQuantity = Math.abs(data.stockOperation.productQuantity);
                    this.stockOperation = data.stockOperation;
                    this.stockProductId = data.stockOperation.stockProduct.id;
                    this.computeQuantities();
                } else {
                    this.resetProduct();
                }
            }
        },
    })
    stockOperation = {
        id: "",
        stockProduct: {
            id: "",
            title: "",
            data: null,
        },
        unitPrice: 0.0,
        price: 0.0,
        productQuantity: 0,
        quantity: 0,
        parent: null,
        accountingLine: null,
        document: null,
        salesPrice: null,
        data: null,
    };

    resetStockOperation() {
        this.stockOperation = JSON.parse(JSON.stringify(this.defaultStockOperation));
        this.stockProductId = "";
        this.productTagId = "";
        this.date = moment().format();
        this.maxQuantity = 0;
        this.notAvailable = false;
        this.operateBy = "Product";
    }

    get disabledSave() {
        if (this.readonly) return true;
        if (!this.isValid) return true;
        if (!this.stockProductId && !this.productTagId) return true;
        if (this.notAvailable) return true;
        return false;
    }

    async save() {
        // debugger
        this.saveLoading = true;
        const stockOp = {
            ...this.stockOperation,
            stockProductId: this.stockProductId,
            date: moment(this.date).utc(true).format(),
            data: null,
        };
        const sign = this.stockOperationType === "INPUT" ? 1 : -1;
        if (this.withQuantity) {
            stockOp.quantity = sign * Math.abs(this.quantity);
            if (this.currentProduct) {
                stockOp.productQuantity = stockOp.quantity * this.currentProduct.unitQuantity;
            }
        } else if (this.withProductQuantity) {
            stockOp.productQuantity = sign * Math.abs(this.productQuantity);
            stockOp.quantity = stockOp.productQuantity / this.currentProduct.unitQuantity;
        } else {
            debugger;
        }
        if (this.quantities.some((q) => q.quantity > 0)) {
            if (!this.stockOperation.data) {
                stockOp.data = { quantities: this.quantities };
            } else {
                stockOp.data["quantities"] = this.quantities;
            }
        }

        stockOp.unitPrice = this.showPrice ? stockOp.unitPrice : null;
        stockOp.price = this.showPrice ? stockOp.price : null;
        stockOp.salesPrice = this.showSalesPrice ? stockOp.salesPrice : null;

        const result = await this.$apollo.mutate({
            mutation: SAVE_STOCK_OPERATION,
            variables: {
                selectedCompanyId: this.companyId,
                stockOperation: stockOp,
                productTagId: this.operateBy === "Family" ? this.productTagId : undefined,
            },
        });
        if (!this.stockOperation.id && result.data && result.data.upsertStockOperation) {
            this.stockOperation.id = result.data.upsertStockOperation.id;
        }
        this.saveLoading = false;
        this.$emit("operation-saved");
    }

    calculatePrice() {
        if (this.stockOperation.unitPrice) {
            this.stockOperation.price = this.stockOperation.unitPrice * this.quantity;
        }
    }

    @Watch("id")
    async onIdChanged(value) {
        if (!value) {
            this.resetStockOperation();
        } else {
            await this.updateQuantity();
        }
    }

    @Watch("type")
    onTypeChanged() {
        this.resetStockOperation();
    }

    @Watch("stockOperation", { deep: true })
    onModifyProduct() {
        // debugger
        this.$emit("stockOperation", this.stockOperation);
    }

    @Watch("stockProductId")
    async onStockProductIdChanged() {
        if (!this.id) {
            const prod = this.products.find((p) => p.id === this.stockProductId);
            if (prod && prod.unitPrice) {
                this.stockOperation.unitPrice = prod.unitPrice;
            } else {
                this.stockOperation.unitPrice = 0;
            }
            await this.updateQuantity();
        }
    }

    get currentProduct() {
        if (this.stockProductId) {
            return this.products.find((p) => p.id === this.stockProductId);
        }
        return null;
    }

    @Watch("operateBy")
    resetFamily(value) {
        this.resetStockOperation();
        this.operateBy = value;
        if (this.operateBy === "Product") {
            this.productTagId = "";
        }
    }

    @Watch("quantity")
    @Watch("stockOperation.unitPrice")
    async onPriceChanged() {
        this.calculatePrice();
    }

    // @Watch("stockOperation.price")
    // onPriceChanged2(){
    //     this.stockOperation.unitPrice = this.stockOperation.price / this.stockOperation.quantity || 0;
    // }

    get showPrice() {
        if (this.stockOperationType !== "INPUT") {
            return false;
        }
        if (this.currentProduct?.type !== "EXPENSE") {
            return false;
        }
        if (this.operateBy !== "Product") {
            return true;
        }
        return true;
    }

    get showSalesPrice() {
        if (this.stockOperationType !== "OUTPUT") {
            return false;
        }
        if (this.currentProduct?.type !== "PRODUCT") {
            return false;
        }
        return true;
    }

    get withProductQuantity() {
        if (this.operateBy === "Product") {
            if (this.currentProduct?.type === "EXPENSE") {
                if (this.stockOperationType === "INPUT") {
                    return false;
                }
            }
        }
        return true;
    }

    get withSubproducts() {
        if (this.operateBy === "Product") {
            if (
                this.currentProduct?.type === "EXPENSE" &&
                this.currentProduct?.data?.quantities?.length
            ) {
                return true;
            }
        }
        return false;
    }

    get withQuantity() {
        return !this.withProductQuantity;
    }
}

export default toNative(StockOperationEdition);
</script>
